import { Empty, Spin,  } from 'antd';
import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import moment, { Moment } from 'moment';
import { DT0Format } from '../../../../generic/StaticVariable';
import { ChartBox } from '../ChartBox';
import { IGetBasisChartV2ApiPayload } from '../../../../typings/chart';
import { IEnrichedMetadata, IGetMetadata } from '../../../../typings/metadata';
import { IBestDataset, IItemsValidation } from '../../../../typings/analytics';
import { MarkerItem } from '../../../../typings/home';
import { datasetType, DownloadCheckboxKey, SCATTER_PLOT_VALIDATION } from '../../../../generic/common';
import Api from '../../../../api/Api';
import { LoadingOutlined } from '@ant-design/icons';
import { UserGroupPermissions } from '../../../../typings/userGroupPermissions';
import { ADVANCED_ANALYTICS } from '../AnalyticsPanel';
import { StoreContext } from '../../../../store/store';
import { isEmpty } from 'underscore';
import { ICreateFullextractionResult } from '../../../../typings/service';
import LayoutPanel from '../LayoutPanel';

interface IScatterPlot {
  validInSituPoint?: boolean
  dateRange?: [moment.Moment, moment.Moment]
  activePoint?: MarkerItem
  enrichedMetadata?: IEnrichedMetadata[]
  insituName?: string
  listParameters: IItemsValidation[],
  activeTab: number
  activeCategoryKey: string
  setActiveCategory: Function
  userGroupPermissions: UserGroupPermissions;
  datasetId: string
  bestDataset?: IBestDataset[]
  activeInsituType: string
  cellSize: string
  waterDepth: string
  headerSection: ReactNode
  notValidInsitu: ReactNode
  datasetChange: (item: string) => void
  variable: string | number
  setVariable: (item: string | number) => void
  startDate: Moment | undefined
  setStartDate: (item: Moment) => void
  endDate: Moment | undefined
  setEndDate: (item: Moment) => void
  onChangeActiveKey: (item: any) => void
}

const ScatterPlot: React.FC<IScatterPlot> = ({
  activeCategoryKey,
  setActiveCategory,
  validInSituPoint,
  activePoint,
  enrichedMetadata,
  insituName,
  listParameters,
  dateRange,
  activeTab,
  userGroupPermissions,
  datasetId,
  bestDataset,
  activeInsituType,
  cellSize,
  waterDepth,
  headerSection,
  notValidInsitu,
  variable,
  setVariable,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  datasetChange,
  onChangeActiveKey
}) => {
  const [scatterPlotImages, setScatterPlotImages] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [tempEnrichedData, setTempEnrichedData] = useState<{Result : ICreateFullextractionResult}>()
  const [tempMetadata, setTempMetadata] = useState({})
  const { datasetBriefs, fwePoints } = useContext(StoreContext)

  const datasets = useMemo(() => {
    const selectedDataset: IBestDataset[] = []
    activePoint?.downloadPage.forEach(element => {
      const options = element.options.map((item) => {
        return {
          id: item.id,
          label: datasetType[element.radioKey],
          value: item.value
        }
      })
      selectedDataset.push(...options)
    });
    if (selectedDataset.length > 0) {
      return selectedDataset
    }
    return []
  }, [activeCategoryKey, activePoint?.downloadPage, activePoint?.coordinates])

  useEffect(() => {
    setScatterPlotImages('')
    setError('')
  }, [activePoint?.coordinates, datasetId])

  useEffect(() => {
    if (enrichedMetadata && listParameters.length > 0 && userGroupPermissions && validInSituPoint && dateRange ) {
      let parameter = listParameters[0].name
      if(!isEmpty(variable)){
        parameter = variable.toString()
      }
      setVariable(parameter)
      setStartDate(moment(dateRange[0]))
      setEndDate(moment(dateRange[1]))
      const values: IGetBasisChartV2ApiPayload = {
        datasetName: datasetId,
        parameters: parameter,
        startDate: startDate?.format(DT0Format) ?? moment(dateRange[0]).format(DT0Format),
        endDate: endDate?.format(DT0Format) ?? moment(dateRange[1]).format(DT0Format),
      };
      if(activeTab === SCATTER_PLOT_VALIDATION && scatterPlotImages === '' && !error && !loading){
        onLoadScatterPlot(values, enrichedMetadata, activeCategoryKey)
      }
    }

  }, [
    activeTab,
    enrichedMetadata,
    datasetId,
    userGroupPermissions,
    validInSituPoint,
    dateRange,
    scatterPlotImages,
    loading,
    error
  ])

  const onLoadScatterPlot = async (
    values: IGetBasisChartV2ApiPayload,
    enrichedMetadata: IEnrichedMetadata[],
    type: string
  ) => {
    setLoading(true);
    setError('');
    setScatterPlotImages('');

    if (activeCategoryKey !== 'SW') {
      setLoading(false);
      return;
    }

    try {
      const category = bestDataset?.find(
        (bd: IBestDataset) => bd.id === values.datasetName
      );

      if (!category) {
        setLoading(false);
        return;
      }

      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === values.datasetName &&
          task.CoordinatesString === activePoint?.coordinates.join()
      );

      if (!task) {
        setLoading(false);
        return;
      }

      if (activeInsituType === 'mood') {
        let metadata : IGetMetadata = tempMetadata as IGetMetadata
        if(isEmpty(metadata)){
          metadata = await Api.metadata.getMetadata(`insitu-${insituName}`) as IGetMetadata
          setTempMetadata(metadata ?? {})
        }
        const findColIndex = metadata.ItemsInfo.find((item) => item.ShortName === values.parameters )?.Index

        let extractionInsituUpload = tempEnrichedData
        if(isEmpty(extractionInsituUpload)){
          extractionInsituUpload = await Api.service.createSubsetExtractionTask(
            `insitu-${insituName}`,
            values.startDate,
            values.endDate,
            activePoint?.coordinates!,
            insituName ?? '',
            cellSize,
            waterDepth,
            datasetBriefs,
            fwePoints
          );

          if (!extractionInsituUpload) {
            setError('Error extraction file');
            setLoading(false);
            return;
          }

          setTempEnrichedData(extractionInsituUpload)
        }

        const title = metadata.ItemsInfo.find((item) => item.ShortName === values.parameters)?.Name

        const res = await Api.chart.getJointScatterPlotImage({
          OrderId: task.OrderId,
          OrderId2: extractionInsituUpload?.Result.OrderIdList[0],
          OriginalOrderId: task.OrderId,
          Longitude: activePoint?.coordinates[0] ?? 0,
          Latitude: activePoint?.coordinates[1] ?? 0,
          StartDate: values.startDate,
          EndDate: values.endDate,
          Title: title ?? '',
          TitleY: title ?? '',
          AcronymX: values.parameters,
          AcronymY: values.parameters,
          ColumnIndexX: findColIndex ?? 1,
          ColumnIndexY: findColIndex ?? 1,
          IntervalCountX: 16,
          IntervalCountY: 16,
          AnalyticType: ADVANCED_ANALYTICS.SCATTER_PLOT_INSITU,
          Duration: '1',
          DatasetId: values.datasetName,
          DatasetId2: `insitu-${insituName}`,
          Sign1: 'below',
          Sign2: 'below',
          Month: 'All',
          PlotFlag: '2',
          PointName: activePoint?.label ?? ''
        });
        if (!res.img) {
          setError(res?.Error?.ErrorMessage ?? 'Error getting scatter plot images');
        } else {
          setScatterPlotImages('data:image/png;base64,' + res.img);
        }
      } else {
        const res = await Api.chart.getScatterPlotInsitu(
          insituName ?? '',
          task.OrderId,
          values.parameters,
          values.startDate,
          values.endDate,
          type,
          values.datasetName
        );

        if (!res) {
          setError(res?.Error?.ErrorMessage ?? 'Error getting scatter plot images');
        } else {
          setScatterPlotImages('data:image/png;base64,' + res.img);
        }
      }
    } catch (error) {
      setError('Error getting scatter plot images');
    } finally {
      setLoading(false);
    }
  }

  const onChangeStartDate = (value: any) => {
    if (value) {
      setStartDate(value);
    }
  };

  const onChangeEndDate = (value: any) => {
    if (value) {
      setEndDate(value);
    }
  };

  const onVariableChange = (value: string | number) => {
    setVariable(value)
  }

  const onChangeParameter = (value: string) => {
    setActiveCategory(value)
  }

  const handleUpdate = () => {
    if (enrichedMetadata && datasetId && startDate && endDate) {
      const values: IGetBasisChartV2ApiPayload = {
        datasetName: datasetId,
        parameters: variable as string,
        startDate: startDate?.format(DT0Format),
        endDate: endDate?.format(DT0Format),
      };
      onLoadScatterPlot(values, enrichedMetadata, activeCategoryKey)
    }
  }

  function disabledStartDate(current: Moment): boolean {
    const minDate = startDate;
    const maxDate = endDate;

    if(!validInSituPoint){
      return true
    }

    if (!current || !maxDate || !minDate) {
      return false;
    }

    const isBeforeMinDate = current.isBefore(minDate, 'day');
    const isAfterMaxDate = current.isAfter(maxDate, 'day');

    return isBeforeMinDate || isAfterMaxDate;
  }

  function disabledEndDate(current: Moment): boolean {
    const maxDate = endDate;

    if(!validInSituPoint){
      return true
    }

    if (!startDate) {
      return true;
    }

    const isBeforeStartDate = current.isBefore(startDate, 'day');
    const isAfterMaxDate = current.isAfter(maxDate, 'day');

    return isBeforeStartDate || isAfterMaxDate;
  }

  return (
   <LayoutPanel
      datasetOptions={datasets}
      point={activePoint?.label}
      dataset={datasetId}
      endDate={endDate}
      startDate={startDate}
      variable={variable}
      showCheckbox={false}
      fieldColumnFields={[headerSection]}
      variableOptions={listParameters}
      onStartDateChange={onChangeStartDate}
      onEndDateChange={onChangeEndDate}
      onVariableChange={onVariableChange}
      disableEndDate={disabledEndDate}
      disableStartDate={disabledStartDate}
      onDatasetChange={datasetChange}
      onPointChange={onChangeActiveKey}
      onUpdate={handleUpdate}
      disabledDataset={!validInSituPoint}
      disabledUpdateButton={!validInSituPoint || activeCategoryKey !== DownloadCheckboxKey.SW}
      disabledVariable={!validInSituPoint || activeCategoryKey !== DownloadCheckboxKey.SW}
      mainContent={
        validInSituPoint ? (<>
        <ChartBox
          actionRow={<></>}
          key={'Scatter Plot'}
          title={'Scatter Plot'}
          height={500}
          plot={
            <>
              {loading && <Spin indicator={<LoadingOutlined style={{ fontSize: 50 }} spin/>} />}
              {!loading && scatterPlotImages.trim() !== "" && (
                <img
                  style={{
                    width: '750px',
                    objectFit: 'scale-down',
                    maxHeight: '65vh',
                  }}
                  src={scatterPlotImages}
                  alt="ScatterPlot"
                  />
                )}
              {!loading && scatterPlotImages === '' && error === '' && validInSituPoint && (
                <Empty />
              )}
              {!loading && error !== '' && validInSituPoint && (
                error
              )}
            </>
          }
          dataset=''
          />
        </>) : notValidInsitu}
   />
  )
}


export default ScatterPlot
